import { SelectOption } from "components/common/FormDialog/types";
import { isSelectOption } from "typeGuards/isSelectOption";
import { isString } from "typeGuards/isString";
import { DatabaseChild, ExtensionInfo, ExtensionSetting } from "./types";

// The server an extension would run on: the PostgreSQL major (undefined = unknown, never
// a reason to refuse) and whether the instance is on the legacy Bitnami image.
export type ExtensionHost = { major?: number; bitnami?: boolean };

export const extensionHost = (
  version?: string,
  imageFlavor?: string
): ExtensionHost => {
  const major = Number.parseInt(version ?? "", 10);
  return {
    major: Number.isNaN(major) ? undefined : major,
    bitnami: imageFlavor === "bitnami"
  };
};

// Mirrors the API's ExtensionCfg.UnavailableReason, which enforces the same rule.
export const extensionUnavailableReason = (
  extension: ExtensionInfo,
  { major, bitnami }: ExtensionHost
): string | undefined => {
  if (extension.official_image_only && bitnami) {
    return "not available on this database yet";
  }
  if (
    major !== undefined &&
    extension.min_major &&
    major < extension.min_major
  ) {
    return `needs PostgreSQL ${extension.min_major} or newer`;
  }
  if (
    major !== undefined &&
    extension.max_major &&
    major > extension.max_major
  ) {
    return `not available above PostgreSQL ${extension.max_major}`;
  }
  return undefined;
};

export const extensionSupportText = ({
  min_major,
  max_major
}: ExtensionInfo): string =>
  min_major && max_major
    ? `PostgreSQL ${min_major}–${max_major}`
    : min_major
      ? `PostgreSQL ${min_major} or newer`
      : max_major
        ? `PostgreSQL ${max_major} or older`
        : "";

export const availableExtensions = (
  catalogue: ExtensionInfo[],
  host: ExtensionHost
): ExtensionInfo[] =>
  catalogue.filter((extension) => !extensionUnavailableReason(extension, host));

// Extensions the server cannot run are not offered, unless already enabled: then they stay
// selectable so they can be removed, with the reason in the label.
export const extensionOptions = (
  catalogue: ExtensionInfo[],
  { current = [], host = {} }: { current?: string[]; host?: ExtensionHost } = {}
): SelectOption[] => [
  ...catalogue.flatMap((extension) => {
    const reason = extensionUnavailableReason(extension, host);
    if (!reason) {
      return [{ label: extension.name, value: extension.name }];
    }
    return current.includes(extension.name)
      ? [{ label: `${extension.name} (${reason})`, value: extension.name }]
      : [];
  }),
  ...current
    .filter((name) => !catalogue.some((extension) => extension.name === name))
    .map((name) => ({ label: `${name} (no longer offered)`, value: name }))
];

// What the picker cannot show per option: the selected extensions' descriptions and the
// ones this server cannot run.
export const extensionDetails = (
  catalogue: ExtensionInfo[],
  host: ExtensionHost,
  selected: string[]
): string => {
  const unavailable = catalogue.flatMap((extension) => {
    const reason = extensionUnavailableReason(extension, host);
    return reason ? [`${extension.name} (${reason})`] : [];
  });
  return [
    ...catalogue
      .filter((extension) => selected.includes(extension.name))
      .map((extension) => {
        const support = extensionSupportText(extension);
        const details = [extension.description, support && `${support}.`]
          .filter(Boolean)
          .join(" ");
        return details && `• ${extension.name} — ${details}`;
      }),
    unavailable.length > 0 && `Not available here: ${unavailable.join(", ")}.`
  ]
    .filter(Boolean)
    .join("\n");
};

export const selectedExtensionNames = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(isSelectOption).map((option) => option.value)
    : [];

export const installedExtensions = (
  children: Pick<
    DatabaseChild,
    "extensions" | "rejected_extensions" | "installed_extensions"
  >[]
): string[] => [
  ...new Set(
    children.flatMap(
      (child) =>
        child.installed_extensions ??
        (child.extensions || []).filter(
          (name) => child.rejected_extensions?.[name] === undefined
        )
    )
  )
];

export type ExtensionState = "applied" | "pending" | "rejected";

// A rejection outranks a pending entry: only the refusal is final.
export const extensionState = (
  child: Pick<
    DatabaseChild,
    "rejected_extensions" | "pending_extensions" | "installed_extensions"
  >,
  name: string
): { state: ExtensionState; reason?: string } => {
  const rejected = child.rejected_extensions?.[name];
  if (rejected !== undefined) return { state: "rejected", reason: rejected };
  const pending = child.pending_extensions?.[name];
  if (pending !== undefined) return { state: "pending", reason: pending };
  return child.installed_extensions &&
    !child.installed_extensions.includes(name)
    ? { state: "pending", reason: "not installed on the server yet" }
    : { state: "applied" };
};

const specExtensions = (children: Pick<DatabaseChild, "extensions">[]) =>
  new Set(children.flatMap((child) => child.extensions || []));

// What an update takes off a database's list; the server keeps every one installed.
export const removedExtensions = (
  before: string[] = [],
  after: string[] = []
): string[] => before.filter((name) => !after.includes(name));

export const keptInstalledNote = (removed: string[]): string | undefined =>
  removed.length > 0
    ? `${removed.join(", ")} ${removed.length === 1 ? "stays" : "stay"} installed: removing an extension from the list never uninstalls it, since that would delete the data that uses it.`
    : undefined;

export const extensionsInUse = (
  retained: string[] = [],
  children: Pick<DatabaseChild, "extensions">[]
): string[] => [...new Set([...retained, ...specExtensions(children)])];

export type StrandedExtension = { name: string; reason: string };

// In-use extensions the running server can run but the upgrade target cannot.
export const strandedByUpgrade = (
  catalogue: ExtensionInfo[],
  inUse: string[],
  from: ExtensionHost,
  to: ExtensionHost
): StrandedExtension[] =>
  catalogue.flatMap((extension) => {
    const reason = extensionUnavailableReason(extension, to);
    return reason &&
      inUse.includes(extension.name) &&
      !extensionUnavailableReason(extension, from)
      ? [{ name: extension.name, reason }]
      : [];
  });

export const upgradeWarning = (
  stranded: StrandedExtension[],
  targetVersion: string
): string =>
  stranded.length > 0
    ? `⚠️ Not available on ${targetVersion}: ${stranded
        .map(({ name, reason }) => `${name} (${reason})`)
        .join(", ")}. The upgrade may be blocked, or leave them uninstalled.`
    : "";

// Only names not already present on the instance: the restart happens once per instance.
export const restartNote = (
  catalogue: ExtensionInfo[],
  selected: string[],
  presentOnInstance: string[] = []
): string => {
  const restarting = selected.filter(
    (name) =>
      !presentOnInstance.includes(name) &&
      catalogue.some(
        (extension) => extension.name === name && extension.restart
      )
  );
  return restarting.length > 0
    ? `⚠️ Enabling ${restarting.join(", ")} restarts the database once.`
    : "";
};

// Settings on a database that no catalogue extension declares. The API replaces a
// database's settings wholesale and keeps such keys only while they are sent back.
export const unofferedSettings = (
  catalogue: ExtensionInfo[],
  current: Record<string, string> = {}
): Record<string, string> => {
  const declared = new Set(
    catalogue.flatMap((extension) =>
      (extension.settings || []).map((setting) => setting.name)
    )
  );
  return Object.fromEntries(
    Object.entries(current).filter(([key]) => !declared.has(key))
  );
};

export const extensionSettings = (
  catalogue: ExtensionInfo[],
  enabled: string[]
): ExtensionSetting[] =>
  catalogue
    .filter((extension) => enabled.includes(extension.name))
    .flatMap((extension) => extension.settings || []);

const INTEGER = /^[+-]?\d+$/;
const REAL = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
const BOOL_VALUES: Record<string, "on" | "off"> = {
  on: "on",
  true: "on",
  yes: "on",
  "1": "on",
  off: "off",
  false: "off",
  no: "off",
  "0": "off"
};

const bound = (value?: string) =>
  isString(value) && value.trim() !== "" ? Number(value) : undefined;

const rangeText = (min?: number, max?: number) =>
  min !== undefined && max !== undefined
    ? ` between ${min} and ${max}`
    : min !== undefined
      ? ` of at least ${min}`
      : max !== undefined
        ? ` of at most ${max}`
        : "";

const sizeBytes = (value?: string): number | undefined => {
  const match = value?.match(
    /^([0-9]+(?:\.[0-9]+)?)(B|kB|KB|MB|GB|TB|KiB|MiB|GiB|TiB)?$/
  );
  if (!match) return undefined;
  const factors: Record<string, number> = {
    B: 1,
    kB: 1024,
    KB: 1024,
    KiB: 1024,
    MB: 2 ** 20,
    MiB: 2 ** 20,
    GB: 2 ** 30,
    GiB: 2 ** 30,
    TB: 2 ** 40,
    TiB: 2 ** 40
  };
  const bytes = Number(match[1]) * factors[match[2] ?? "B"];
  return Number.isFinite(bytes) && Number.isInteger(bytes) ? bytes : undefined;
};

const parseSettingValue = (
  setting: ExtensionSetting,
  raw: string
): { value: string } | { error: string } => {
  const text = raw.trim();
  if (
    (text === "" && setting.allowEmpty) ||
    setting.specialValues?.includes(text)
  )
    return { value: text };
  switch (setting.type) {
    case "integer":
    case "real": {
      const min = bound(setting.min);
      const max = bound(setting.max);
      const isInteger = setting.type === "integer";
      const number = Number(text);
      return (isInteger ? INTEGER : REAL).test(text) &&
        (min === undefined || number >= min) &&
        (max === undefined || number <= max)
        ? { value: isInteger ? BigInt(text).toString() : String(number) }
        : {
            error: `Must be ${isInteger ? "an integer" : "a number"}${rangeText(min, max)}`
          };
    }
    case "set": {
      const values = text.toUpperCase().split(",");
      return values.every((value) => setting.values?.includes(value))
        ? { value: [...new Set(values)].sort().join(",") }
        : {
            error: `Use a comma-separated list of: ${(setting.values || []).join(", ")}`
          };
    }
    case "flags":
      return [...text].every((value) => setting.values?.includes(value))
        ? { value: [...new Set(text)].sort().join("") }
        : { error: `Use only these flags: ${(setting.values || []).join("")}` };
    case "bool": {
      const value = BOOL_VALUES[text.toLowerCase()];
      return value ? { value } : { error: "Must be on or off" };
    }
    case "timezone":
    case "enum":
      return setting.values?.includes(text)
        ? { value: text }
        : { error: `Must be one of: ${(setting.values || []).join(", ")}` };
    case "size": {
      const value = sizeBytes(text),
        min = sizeBytes(setting.min),
        max = sizeBytes(setting.max);
      return value !== undefined &&
        (min === undefined || value >= min) &&
        (max === undefined || value <= max)
        ? { value: String(value) }
        : { error: `Must be a size${rangeText(min, max)} bytes` };
    }
    default:
      return { error: `Unsupported setting type "${String(setting.type)}"` };
  }
};

// Empty means "use the extension default" and is never an error.
export const settingValueError = (
  setting: ExtensionSetting,
  raw: string
): string | undefined => {
  if (raw.trim() === "" && !setting.allowEmpty) return undefined;
  const result = parseSettingValue(setting, raw);
  return "error" in result ? result.error : undefined;
};

export const normalizeSettingValue = (
  setting: ExtensionSetting,
  raw: string
): string | undefined => {
  if (raw.trim() === "" && !setting.allowEmpty) return undefined;
  const result = parseSettingValue(setting, raw);
  return "value" in result ? result.value : undefined;
};

// Form field names must not contain "." (react-hook-form reads it as a path); setting names never contain ":".
export const settingFieldName = (name: string): string =>
  name.replace(/\./g, ":");

const fieldText = (value: unknown): string =>
  isSelectOption(value) ? value.value : isString(value) ? value : "";

// A field holds the default until changed, and the default is what an unset setting
// already is: sending it would pin a value instead of inheriting it.
export const buildSettingsPayload = (
  settings: ExtensionSetting[],
  values: Record<string, unknown>
): Record<string, string> =>
  Object.fromEntries(
    settings.flatMap((setting) => {
      const value = normalizeSettingValue(
        setting,
        fieldText(values[settingFieldName(setting.name)])
      );
      const isDefault =
        setting.default !== undefined &&
        value === normalizeSettingValue(setting, setting.default);
      return value === undefined || isDefault ? [] : [[setting.name, value]];
    })
  );
